<template>
  <div class="combo-chart" ref="chart" style="width: 100%; height: 100%;"></div>
</template>

<script>
export default {
  name: 'ComboChart',
  props: {
    data: {
      type: Array,
      default: () => []
    },
    displayCount: {
      type: Number,
      default: 9
    }
  },
  data() {
    return {
      chart: null
    }
  },
  watch: {
    data: {
      handler() {
        this.updateChart()
      },
      deep: true
    }
  },
  mounted() {
    this.initChart()
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    if (this.chart) {
      this.chart.dispose()
      this.chart = null
    }
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    initChart() {
      this.chart = this.$echarts.init(this.$refs.chart)
      this.$nextTick(() => {
        this.updateChart()
        this.handleResize()
      })
    },
    updateChart() {
      if (!this.chart) return

      const xData = this.data.map(item => item.name)
      const barData = this.data.map(item => this.toFiniteNumber(item.barValue))
      const rawLineData = this.data.map(item => this.toFiniteNumber(item.lineValue))
      const lineData = rawLineData.map(value => this.compressPercentValue(value))
      const endValue = Math.max(Math.min(this.displayCount - 1, xData.length - 1), 0)
      const hasData = xData.length > 0
      const barAxisRange = this.getAxisRange(barData, 10000)
      const lineAxisRange = this.getAxisRange(lineData, 0.5)

      const option = {
        title: hasData ? undefined : {
          text: '暂无数据',
          left: 'center',
          top: 'middle',
          textStyle: {
            color: '#999',
            fontSize: 14,
            fontWeight: 'normal'
          }
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          },
          formatter: function(params) {
            let result = params[0].name + '月<br/>';
            params.forEach(item => {
              if (item.seriesType === 'bar') {
                result += `${item.marker} 金额: ${Number(item.value).toLocaleString()}<br/>`;
              } else if (item.seriesType === 'line') {
                const rawValue = item.data && item.data.rawValue !== undefined ? item.data.rawValue : item.value
                result += `${item.marker} 比例: ${(rawValue * 100).toFixed(2)}%<br/>`;
              }
            });
            return result;
          }
        },
        grid: {
          left: 20,
          right: 24,
          bottom: 50,
          top: 24,
          containLabel: true
        },
        dataZoom: hasData ? [
          {
            type: 'slider',
            show: true,
            xAxisIndex: [0],
            startValue: 0,
            endValue,
            height: 8,
            bottom: 12,
            fillerColor: '#c5c5c5',
            borderColor: 'transparent',
            backgroundColor: '#f1f1f1',
            handleSize: 0,
            showDetail: false,
            showDataShadow: false
          },
          {
            type: 'inside',
            xAxisIndex: [0],
            startValue: 0,
            endValue
          }
        ] : [],
        xAxis: {
          type: 'category',
          data: xData,
          axisTick: {
            show: false
          },
          axisLine: {
            show: false
          },
          axisLabel: {
            interval: 0,
            color: '#666',
            fontSize: 12,
            margin: 15
          }
        },
        yAxis: [
          {
            type: 'value',
            min: barAxisRange.min,
            minInterval: 1,
            max: barAxisRange.max,
            splitLine: {
              show: true,
              lineStyle: {
                type: 'dashed',
                color: '#EEEEEE'
              }
            },
            axisLabel: {
              color: '#999',
              formatter: (value) => {
                if (value === 0) return '0'
                const tenThousand = value / 10000
                return `${Number.isInteger(tenThousand) ? tenThousand : tenThousand.toFixed(2)}w`
              }
            }
          },
          {
            type: 'value',
            min: lineAxisRange.min,
            max: lineAxisRange.max,
            splitLine: {
              show: false
            },
            axisLabel: {
              color: '#999',
              formatter: (value) => {
                const rawValue = this.restorePercentValue(value)
                if (rawValue === 0) return '0%'
                return `${(rawValue * 100).toFixed(0)}%`
              }
            }
          }
        ],
        series: [
          {
            type: 'bar',
            name: '金额',
            barWidth: '24px',
            barMinHeight: 2,
            itemStyle: {
              color: new this.$echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: '#70A4F8' },
                { offset: 1, color: '#3B7BDF' }
              ]),
              borderRadius: [4, 4, 0, 0]
            },
            markLine: {
              symbol: 'none',
              silent: true,
              label: { show: false },
              lineStyle: {
                color: '#3B7BDF',
                type: 'dashed',
                width: 1
              },
              data: [{ yAxis: 0 }]
            },
            data: barData
          },
          {
            type: 'line',
            name: '比例',
            yAxisIndex: 1,
            smooth: true,
            symbol: 'none',
            lineStyle: {
              color: '#70A4F8',
              width: 2
            },
            areaStyle: {
              color: new this.$echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: 'rgba(112, 164, 248, 0.2)' },
                { offset: 1, color: 'rgba(112, 164, 248, 0)' }
              ])
            },
            data: lineData.map((value, index) => ({
              value,
              rawValue: rawLineData[index]
            }))
          }
        ]
      }
      this.chart.clear()
      this.chart.setOption(option, true)
      this.$nextTick(this.handleResize)
    },
    toFiniteNumber(value) {
      const num = Number(value)
      return Number.isFinite(num) ? num : 0
    },
    compressPercentValue(value) {
      const sign = value < 0 ? -1 : 1
      return sign * Math.sqrt(Math.abs(value))
    },
    restorePercentValue(value) {
      const sign = value < 0 ? -1 : 1
      return sign * Math.pow(Math.abs(value), 2)
    },
    getAxisRange(data, step) {
      const maxAbsValue = Math.max(...data.map(value => Math.abs(value)), 0)
      const limit = maxAbsValue === 0 ? step : Math.ceil(maxAbsValue / step) * step
      return {
        min: -limit,
        max: limit
      }
    },
    handleResize() {
      if (this.chart) {
        this.chart.resize()
      }
    }
  }
}
</script>

<style scoped>
.combo-chart {
  width: 100%;
  height: 100%;
  min-height: 360px;
}
</style>
